// Contrats — Période d'essai & CDD shared vocabulary. Helpers ported
// from the design prototype's ct-data.jsx minus its demo dataset and
// localStorage reminder store: all real data comes from /api/contracts
// (milestone state lives in ContractMilestone rows server-side).
import { cgD, cgToday } from '../conges/common.js';
import type { ContractCddDTO, ContractEssaiDTO, ContractMilestoneDTO } from '../../api/hooks.js';

export type ContractKind = 'essai' | 'cdd';

// One row shape for both tabs — mirrors the proto's `p` objects.
export interface ContractRow {
  kind: ContractKind;
  employee: ContractEssaiDTO['employee'];
  start: string | null;
  end: string; // fin d'essai / terme du CDD
  status: string; // stored: encours|confirme|rompu / actif|cdi|nonrenouvele|termine
  outcomeAt: string | null;
  dateSortie: string | null;
  notes: string | null;
  milestones: ContractMilestoneDTO[];
  // essai
  months?: number;
  renewMonths?: number | null;
  // cdd
  motif?: string | null;
  renewals?: number;
}

export const essaiRow = (e: ContractEssaiDTO): ContractRow => ({ kind: 'essai', ...e, end: e.term });
export const cddRow = (c: ContractCddDTO): ContractRow => ({ kind: 'cdd', ...c });

// ── date math (YYYY-MM-DD strings) ───────────────────────────
export const ctDays = (a: string, b: string) => Math.round((cgD(b).getTime() - cgD(a).getTime()) / 86_400_000);
export const ctPct = (a: string, b: string, c: string) =>
  Math.max(0, Math.min(100, (ctDays(a, c) / Math.max(1, ctDays(a, b))) * 100));
export const ctDur = (n: number) => {
  const a = Math.abs(n);
  if (a < 31) return `${a} j`;
  const m = Math.floor(a / 30.44);
  return m < 12 ? `${m} mois` : `${Math.floor(m / 12)} an${m >= 24 ? 's' : ''}${m % 12 ? ` ${m % 12} m` : ''}`;
};

// ── status ───────────────────────────────────────────────────
export const ctActive = (p: ContractRow) => p.status === 'encours' || p.status === 'actif';
// Display status: stored outcomes pass through; active contracts get an
// urgency bucket from the days left to term.
export const ctStatus = (p: ContractRow, today: string): string => {
  if (!ctActive(p)) return p.status;
  const dl = ctDays(today, p.end);
  return dl < 0 ? 'depasse' : dl <= 7 ? 'critique' : dl <= 30 ? 'urgent' : dl <= 90 ? 'proche' : 'encours';
};
export const CT_ST: Record<string, [string, string]> = {
  encours: ['En cours', ''], actif: ['En cours', ''],
  proche: ['Échéance proche', 'info'], urgent: ["Moins d'un mois", 'warn'],
  critique: ['Dernière semaine', 'bad'], depasse: ['Échéance dépassée', 'bad'],
  confirme: ['Confirmé en CDI', 'green'], rompu: ['Essai rompu', 'bad'],
  cdi: ['Transformé en CDI', 'green'], termine: ['Contrat terminé', ''], nonrenouvele: ['Non renouvelé', 'bad'],
};

// ── milestone metadata (proto CT_STEPS) ──────────────────────
export interface StepMeta {
  lbl: string;
  sub: string;
  tone: 'info' | 'warn' | 'bad';
  to: string; // recipients label — BOSS/ADMIN in the real app
  tpl: string;
}
export const CT_STEP_META: Record<ContractKind, Record<string, StepMeta>> = {
  essai: {
    mi: { lbl: 'Évaluation mi-parcours', sub: "moitié de l'essai écoulée", tone: 'info', to: 'RH + Direction', tpl: "Grille d'évaluation mi-parcours à compléter sous 7 jours." },
    j30: { lbl: 'Décision à préparer', sub: "1 mois avant la fin d'essai", tone: 'warn', to: 'RH + Direction', tpl: 'Confirmer, renouveler ou rompre — arbitrage attendu avant J-15.' },
    j7: { lbl: 'Notification au salarié', sub: "7 jours avant la fin d'essai", tone: 'bad', to: 'RH + Direction', tpl: 'Courrier de confirmation ou de rupture à remettre en main propre.' },
  },
  cdd: {
    j90: { lbl: 'Point de renouvellement', sub: '3 mois avant le terme', tone: 'info', to: 'RH + Direction', tpl: 'Le poste doit-il être reconduit ? Budget à valider.' },
    j30: { lbl: 'Préavis & décision', sub: '1 mois avant le terme', tone: 'warn', to: 'RH + Direction', tpl: 'Notifier le renouvellement ou le non-renouvellement au salarié.' },
    j7: { lbl: 'Sortie & solde de tout compte', sub: '7 jours avant le terme', tone: 'bad', to: 'RH + Paie', tpl: 'Certificat de travail, solde de tout compte, restitution du matériel.' },
  },
};
export const stepMeta = (kind: ContractKind, code: string): StepMeta =>
  CT_STEP_META[kind][code] ?? { lbl: code, sub: '', tone: 'info', to: 'RH', tpl: '' };

// ── reminder engine (proto ctReminders, server state) ────────
export type ReminderState = 'due' | 'snoozed' | 'soon' | 'sent' | 'done' | 'future';
export interface ReminderItem {
  key: string;
  p: ContractRow;
  code: string;
  date: string;
  meta: StepMeta;
  ms: ContractMilestoneDTO;
  late: number; // days past the milestone date (negative = future)
  state: ReminderState;
}

// A reminder is « due » when its date is reached, the contract is still
// running and nobody has sent/handled it (a lapsed snooze re-arms it).
export function ctReminders(rows: ContractRow[], today = cgToday()): ReminderItem[] {
  const out: ReminderItem[] = [];
  for (const p of rows) {
    if (!ctActive(p)) continue;
    for (const ms of p.milestones) {
      const meta = stepMeta(p.kind, ms.code);
      const st = ms.state;
      const snoozed = st?.status === 'snoozed' && (st.snoozeUntil ?? '') > today;
      const due = ms.date <= today && (!st || st.status === 'snoozed') && !snoozed;
      const soon = ms.date > today && ctDays(today, ms.date) <= 30;
      out.push({
        key: `${p.kind}:${p.employee.id}:${ms.code}`,
        p, code: ms.code, date: ms.date, meta, ms,
        late: ctDays(ms.date, today),
        state: due ? 'due' : snoozed ? 'snoozed' : st ? (st.status as ReminderState) : soon ? 'soon' : 'future',
      });
    }
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : 1));
}
export const ctDue = (rows: ContractRow[], today?: string) => ctReminders(rows, today).filter((r) => r.state === 'due');
export const ctSoon = (rows: ContractRow[], today?: string) =>
  ctReminders(rows, today).filter((r) => r.state === 'soon' || r.state === 'snoozed');
